import { Classes, Colors } from "@blueprintjs/core";
import uniqBy from "lodash/uniqBy";

import { Box } from "@/components/layout/flexbox";
import { MIDTEXT_COLOR } from "@/css/constants";
import { useWorkspaceMemberDetailsQuery } from "@/graphql";
import { StatsMember, WorkspaceGroup } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { formatCredits } from "@/utils/string";
import { NonNullableArrayItem } from "@/utils/types";

type Configuration = NonNullable<NonNullable<WorkspaceGroup["configuration"]>>;

type CoachSpec = NonNullableArrayItem<
  NonNullable<NonNullable<WorkspaceGroup["configuration"]>["coachSpecIncludes"]>
>;

export enum ConfigType {
  PERSONAL = "PERSONAL",
  GROUP = "GROUP",
  MEMBER = "MEMBER",
  WORKSPACE = "WORKSPACE",
  APP = "APP",
  COMBINED = "COMBINED",
}

function listCoachSpecialty(coachSpecs: CoachSpec[], isCannot: boolean) {
  const specs = ensureNonNullArray(
    uniqBy(
      coachSpecs.map((item) => item.specialty),
      "id",
    ),
  );

  return specs.map((spec) => {
    const coaches = ensureNonNullArray(
      coachSpecs.filter((item) => item.specialty === spec),
    );

    const coachesText = coaches.map((item) => item.coach?.name).join(", ");

    return (
      <li key={`spec-${spec?.id}`}>
        <b style={{ color: isCannot ? MIDTEXT_COLOR : Colors.GREEN3 }}>
          {isCannot ? "Cannot" : "Can only"}
        </b>{" "}
        work with {coachesText} as a {spec?.name}
      </li>
    );
  });
}

interface PermissionDescriptionProps {
  configuration: Configuration;
  user?: StatsMember["user"];
  group?: WorkspaceGroup;
  workspace?: StatsMember["workspace"];
}

function PermissionDescription({
  configuration,
  user,
  group,
  workspace,
}: PermissionDescriptionProps) {
  const { configType } = configuration || {};

  let configName = "";
  if (configType === ConfigType.GROUP) {
    configName = `Permissions inherited from group: ${group?.name}`;
  } else if (configType === ConfigType.WORKSPACE) {
    configName = `Permissions inherited from ${workspace?.name} workspace `;
  } else if (
    configType === ConfigType.PERSONAL ||
    configType === ConfigType.MEMBER
  ) {
    configName = "Permissions are custom for this user";
  } else if (configType === ConfigType.COMBINED) {
    configName = `This user's permissions are the combination of multiple groups`;
  }

  if (!configuration) {
    return (
      <span style={{ fontStyle: "italic", color: "#778397" }}>
        Default permissions within the workspace.
      </span>
    );
  }

  if (user) {
    return <span style={{ color: Colors.BLACK }}>{configName}</span>;
  }
  return null;
}

interface MemberPermissionDisplayProps {
  user: StatsMember["user"];
  group: WorkspaceGroup;
  workspace: StatsMember["workspace"];
}

export function MemberPermissionDisplay({
  user,
  group,
  workspace,
}: MemberPermissionDisplayProps) {
  const { data, loading } = useWorkspaceMemberDetailsQuery({
    skip: !user,
    variables: {
      customerId: user && user.id,
    },
  });

  const { workspaceMember } = data || {};
  const { configuration } = workspaceMember || {};

  if (!configuration) return null;

  return (
    <Box>
      <Box fontSize={20} fontWeight={600}>
        Permissions
      </Box>
      <Box mt={2}>
        {loading ? (
          <Box className={Classes.SKELETON} style={{ height: 100 }}></Box>
        ) : null}
      </Box>
      <Box mt={2}>
        <PermissionDisplay
          configuration={configuration}
          user={user}
          group={group}
          workspace={workspace}
        />
      </Box>
    </Box>
  );
}

interface PermissionDisplayProps {
  configuration: Configuration;
  user?: StatsMember["user"];
  group?: WorkspaceGroup;
  workspace?: StatsMember["workspace"];
}

export function PermissionDisplay({
  configuration,
  user,
  group,
  workspace,
}: PermissionDisplayProps) {
  if (!configuration) {
    return null;
  }
  const { maximumCreditPrice, enableJobSearch, enableReviews } = configuration;

  const coachSpecIncludes = ensureNonNullArray(
    configuration?.coachSpecIncludes,
  );

  const coachSpecExcludes = ensureNonNullArray(
    configuration?.coachSpecExcludes,
  );

  const specialtyIncludes = ensureNonNullArray(
    configuration?.specialtyIncludes,
  );

  const specialtyExcludes = ensureNonNullArray(
    configuration?.specialtyExcludes,
  );

  return (
    <Box>
      <PermissionDescription
        configuration={configuration}
        user={user}
        group={group}
        workspace={workspace}
      />
      <ul className="list-disc pl-6" style={{ margin: 0, paddingLeft: 20 }}>
        {specialtyIncludes.length ? (
          <li>
            <b style={{ color: Colors.GREEN3 }}>Can only</b> access{" "}
            {specialtyIncludes.map((item) => item.name).join(", ")}
          </li>
        ) : (
          specialtyExcludes.length > 0 && (
            <li>
              <b style={{ color: MIDTEXT_COLOR }}>Cannot</b> access{" "}
              {specialtyExcludes.map((item) => item.name).join(", ")}
            </li>
          )
        )}

        {coachSpecIncludes.length > 0 &&
          listCoachSpecialty(coachSpecIncludes, false)}

        {coachSpecExcludes.length > 0 &&
          listCoachSpecialty(coachSpecExcludes, true)}

        {maximumCreditPrice && (
          <li>
            <b style={{ color: Colors.GREEN3 }}>Can only</b>
            &nbsp;book services that cost up to{" "}
            {formatCredits(maximumCreditPrice)}.
          </li>
        )}
        <li>
          <b style={{ color: enableReviews ? Colors.GREEN3 : MIDTEXT_COLOR }}>
            {enableReviews ? "Can" : "Cannot"}
          </b>{" "}
          access review or assessment products
        </li>
        {enableJobSearch && (
          <li>
            <b style={{ color: Colors.GREEN3 }}>Can</b> access job search
            features
          </li>
        )}
      </ul>
    </Box>
  );
}
